import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { LogIn, Loader2 } from "lucide-react";

import { api, sessionFromLogin, type LoginResponse } from "@/lib/api";
import { useAuth } from "@/stores/auth";
import { fa } from "@/locales/fa";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export function LoginPage() {
  const { session, setSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const mutation = useMutation({
    mutationFn: (vars: { username: string; password: string }) =>
      api<LoginResponse>("/auth/login", {
        method: "POST",
        unauthenticated: true,
        body: JSON.stringify({
          username: vars.username.trim().toLowerCase(),
          password: vars.password,
        }),
      }),
    onSuccess: (res) => {
      setSession(sessionFromLogin(res));
      const to = (location.state as { from?: string } | null)?.from ?? "/dashboard";
      navigate(to, { replace: true });
    },
    onError: (err: Error) => {
      toast.error(fa.auth.invalid, { description: err.message });
    },
  });

  if (session) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-grid grid place-items-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center gap-3">
          <div className="grid place-items-center size-11 rounded-xl bg-white ring-1 ring-border shadow-sm">
            <img
              src="/logo.webp"
              alt={fa.appName}
              className="size-8 object-contain"
              draggable={false}
            />
          </div>
          <div>
            <div className="text-base font-semibold">{fa.appName}</div>
            <div className="text-[12px] text-muted-foreground">سامانه مدیریتی بکاپ آف‌سایت</div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{fa.auth.title}</CardTitle>
            <CardDescription>{fa.auth.subtitle}</CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="grid gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                mutation.mutate({ username, password });
              }}
            >
              <div className="grid gap-2">
                <Label htmlFor="username">{fa.auth.username}</Label>
                <Input
                  id="username"
                  type="text"
                  className="lat-num"
                  autoComplete="username"
                  required
                  minLength={1}
                  maxLength={64}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  dir="ltr"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password">{fa.auth.password}</Label>
                <Input
                  id="password"
                  type="password"
                  className="lat-num"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  dir="ltr"
                />
              </div>

              <Button type="submit" disabled={mutation.isPending} className="mt-2">
                {mutation.isPending ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    <span>{fa.auth.submitting}</span>
                  </>
                ) : (
                  <>
                    <LogIn className="size-4" />
                    <span>{fa.auth.submit}</span>
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="mt-4 text-center text-[11px] text-muted-foreground">
          © میزبان‌فا — تمامی حقوق محفوظ است.
        </p>
      </div>
    </div>
  );
}
